import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { Icon } from "@project/ui";
import type { IconProps } from "@project/ui";
import { useForm } from "react-hook-form";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import {
  createExpenseNewSchema,
  type ExpenseNewFormValues,
} from "@apps/dashboard/lib/expense-new-schema";
import {
  expenseSplitsFromMembers,
  membersUseConfiguredSplitBps,
  splitMinorByShareBps,
} from "@apps/dashboard/lib/expense-splits";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import {
  createExpense,
  createRecurringExpenses,
  fetchExpenseCategories,
  fetchFinancialProfile,
  fetchHouseMembers,
  fetchReceiptScan,
  homeExpenseQueryKeys,
  patchReceiptScan,
} from "@apps/dashboard/lib/home-expense-api";
import { minorToMajorInputValue } from "@apps/dashboard/lib/parse-amount";
import {
  matchCategoryId,
  notesFromExtraction,
} from "@apps/dashboard/lib/receipt-scan-prefill";
import {
  isReceiptExtractionErrorPayload,
  parseReceiptExtractedPayload,
} from "@project/core";
import type { ExpensePaymentMethod } from "@apps/dashboard/lib/home-expense-types";
import { parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import {
  PAID_BY_JOINT_SELECT_VALUE,
  paidByPayloadFromSelectValue,
} from "@apps/dashboard/lib/paid-by-select";
import {
  addCalendarMonths,
  endsOnForMonthlyFixedWindow,
  EXPENSE_SERIES_MATERIALIZE_MONTHS_INITIAL,
  inclusiveCalendarMonthSpan,
  listOccurrenceDates,
  MONTHLY_FIXED_MATERIALIZED_COUNT,
  monthsStepForFrequency,
  splitTotalMinorAcrossOccurrences,
} from "@apps/dashboard/lib/recurring-dates";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { ExpenseSplitBreakdownCard } from "@apps/dashboard/components/expense/ExpenseSplitBreakdownCard";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { FilePondStorageUpload } from "@apps/dashboard/components/FilePondStorageUpload";
import {
  EXPENSE_CREATE_WIZARD_REVIEW_STEP,
  parseExpenseCreateFlowParam,
  type ExpenseCreateFlow,
} from "@apps/dashboard/features/expenses/expense-create-flow";
import {
  readLastExpenseCreateFlow,
  writeLastExpenseCreateFlow,
} from "@apps/dashboard/features/expenses/expense-last-flow";
import { trackDashboardEvent } from "@apps/dashboard/lib/analytics";

function expenseFlowTitleKey(flow: Exclude<ExpenseCreateFlow, "pick">): string {
  if (flow === "quick") return "dashboardUi.expenses.flows.intentQuickTitle";
  if (flow === "monthly") return "dashboardUi.expenses.flows.intentRecurringTitle";
  return "dashboardUi.expenses.flows.intentSeriesTitle";
}

function expenseFlowIcon(flow: Exclude<ExpenseCreateFlow, "pick">): IconProps["name"] {
  if (flow === "quick") return "ReceiptText";
  if (flow === "monthly") return "Repeat";
  return "CalendarRange";
}

function todayLocalIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const defaultValues: ExpenseNewFormValues = {
  title: "",
  amountInput: "",
  currency: "EUR",
  occurredOn: todayLocalIsoDate(),
  categoryId: "",
  paymentMethod: "",
  shared: false,
  isRecurring: false,
  recurrenceKind: "monthly_fixed",
  endsOn: "",
  frequency: "monthly",
  prorated: false,
  paidByUserId: "",
  notes: "",
  paidNow: false,
};

export default function ExpenseNewPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromScan = searchParams.get("fromScan")?.trim() ?? "";
  const createFlow: ExpenseCreateFlow = useMemo(
    () => parseExpenseCreateFlowParam(searchParams.get("flow"), { fromScan: Boolean(fromScan) }),
    [searchParams, fromScan],
  );
  const [wizardStep, setWizardStep] = useState(0);
  const [quickMoreOpen, setQuickMoreOpen] = useState(false);
  const [savedLastFlow, setSavedLastFlow] = useState<ExpenseCreateFlow | null>(null);
  const [receiptStorageKey, setReceiptStorageKey] = useState<string | null>(null);
  const [receiptPondKey, setReceiptPondKey] = useState(0);
  const isWizardFlow = createFlow === "monthly" || createFlow === "series";
  const atWizardReview = isWizardFlow && wizardStep >= EXPENSE_CREATE_WIZARD_REVIEW_STEP;
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useSyncHouseFromParams(houseId);
  const prefillAppliedRef = useRef<string | null>(null);

  useEffect(() => {
    prefillAppliedRef.current = null;
  }, [fromScan]);

  useEffect(() => {
    setSavedLastFlow(readLastExpenseCreateFlow());
  }, []);

  const profileQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.financialProfile(locale, houseId) : ["fp", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchFinancialProfile(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const categoriesQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.categories(locale, houseId) : ["cat", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchExpenseCategories(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const membersQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.houseMembers(locale, houseId) : ["mem", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchHouseMembers(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const receiptScanQuery = useQuery({
    queryKey:
      houseId && fromScan
        ? homeExpenseQueryKeys.receiptScan(locale, houseId, fromScan)
        : ["receipt-scan", "noop"],
    queryFn: () => {
      if (!houseId || !fromScan) throw new Error("noop");
      return fetchReceiptScan(locale, houseId, fromScan);
    },
    enabled: Boolean(houseId && fromScan),
  });

  const categories = categoriesQuery.data ?? [];
  const members = membersQuery.data ?? [];

  const schema = useMemo(() => createExpenseNewSchema(t, categories), [t, categories]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ExpenseNewFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onSubmit",
  });

  const values = watch();

  useEffect(() => {
    if (values.isRecurring) {
      setReceiptStorageKey(null);
      setReceiptPondKey((k) => k + 1);
    }
  }, [values.isRecurring]);

  useEffect(() => {
    setWizardStep(0);
  }, [createFlow]);

  useEffect(() => {
    if (createFlow === "quick") {
      setValue("isRecurring", false, { shouldValidate: false });
    } else if (createFlow === "monthly") {
      setValue("isRecurring", true, { shouldValidate: false });
      setValue("recurrenceKind", "monthly_fixed", { shouldValidate: false });
      setValue("prorated", false, { shouldValidate: false });
    } else if (createFlow === "series") {
      setValue("isRecurring", true, { shouldValidate: false });
      setValue("recurrenceKind", "series", { shouldValidate: false });
    }
  }, [createFlow, setValue]);

  const currencySeeded = useRef(false);
  useEffect(() => {
    if (!currencySeeded.current && profileQuery.data?.currency) {
      setValue("currency", profileQuery.data.currency, { shouldValidate: false });
      currencySeeded.current = true;
    }
  }, [profileQuery.data, setValue]);

  useEffect(() => {
    if (values.recurrenceKind === "monthly_fixed") {
      clearErrors("endsOn");
    }
  }, [values.recurrenceKind, clearErrors]);

  useEffect(() => {
    if (!fromScan || !houseId || categories.length === 0) return;
    const scan = receiptScanQuery.data;
    if (!scan || scan.status !== "completed") return;
    if (prefillAppliedRef.current === fromScan) return;
    const raw = scan.extractedPayload;
    if (!raw || isReceiptExtractionErrorPayload(raw)) return;
    const parsed = parseReceiptExtractedPayload(raw);
    if (!parsed) return;
    prefillAppliedRef.current = fromScan;
    if (parsed.merchant?.trim()) {
      setValue("title", parsed.merchant.trim(), { shouldValidate: false });
    }
    if (typeof parsed.totalAmountMinor === "number" && parsed.totalAmountMinor >= 0) {
      setValue("amountInput", minorToMajorInputValue(parsed.totalAmountMinor), {
        shouldValidate: false,
      });
    }
    if (parsed.currency && /^[A-Z]{3}$/i.test(parsed.currency)) {
      setValue("currency", parsed.currency.toUpperCase(), { shouldValidate: false });
    }
    if (parsed.occurredOn) {
      setValue("occurredOn", parsed.occurredOn, { shouldValidate: false });
    }
    const catId = matchCategoryId(categories, parsed);
    if (catId) {
      setValue("categoryId", catId, { shouldValidate: false });
    }
    const noteExtra = notesFromExtraction(parsed);
    if (noteExtra) {
      setValue("notes", noteExtra, { shouldValidate: false });
    }
  }, [
    fromScan,
    houseId,
    categories,
    receiptScanQuery.data,
    setValue,
  ]);

  const resolvedTitle = useMemo(() => {
    const t0 = values.title.trim();
    if (t0) return t0;
    const cat = categories.find((c) => c.id === values.categoryId);
    return cat?.name.trim() ?? "";
  }, [values.title, values.categoryId, categories]);

  const seriesEndsFieldHelp = useMemo(() => {
    if (values.recurrenceKind !== "series") return "";
    if (!values.prorated) {
      return t("dashboardUi.expenses.form.recurringEndsOptionalHelp");
    }
    const step = monthsStepForFrequency(values.frequency);
    if (step <= 1) {
      return t("dashboardUi.expenses.form.recurringEndsRequiredWhenProratedHelp");
    }
    return t("dashboardUi.expenses.form.recurringEndsProrateOpenPeriodHelp");
  }, [values.recurrenceKind, values.prorated, values.frequency, t]);

  const wizardBanner = useMemo(() => {
    if (!isWizardFlow || atWizardReview) return null;
    const prefix =
      createFlow === "monthly"
        ? "dashboardUi.expenses.flows.monthlyWizard."
        : "dashboardUi.expenses.flows.seriesWizard.";
    const titles = ["step1Title", "step2Title", "step3Title", "step4Title", "step5Title"] as const;
    const helps = ["step1Help", "step2Help", "step3Help", "step4Help", "step5Help"] as const;
    const i = Math.min(wizardStep, 4) as 0 | 1 | 2 | 3 | 4;
    return { title: t(`${prefix}${titles[i]}`), help: t(`${prefix}${helps[i]}`) };
  }, [isWizardFlow, atWizardReview, createFlow, wizardStep, t]);

  const stepUi = useMemo(() => {
    const q = createFlow === "quick";
    const m = createFlow === "monthly";
    const s = createFlow === "series";
    const st = wizardStep;
    const rev = atWizardReview;
    return {
      showAmount: q || (m && (st === 1 || rev)) || (s && (st === 0 || rev)),
      showCurrencyDateGrid: q || (s && (st === 0 || rev)) || (m && rev),
      showCurrencyOnlyRow: m && st === 1 && !rev,
      showDateOnlyRow: m && st === 2 && !rev,
      showCategory: q || (m && (st === 0 || rev)) || (s && (st === 0 || rev)),
      showTitle: (q && quickMoreOpen) || (m && (st === 0 || rev)) || (s && (st === 0 || rev)),
      showPayment: q || (m && (st === 3 || rev)) || (s && (st === 4 || rev)),
      showPayerShared:
        (q && quickMoreOpen) || (m && (st === 4 || rev)) || (s && (st === 4 || rev)),
      showQuickMoreToggle: q,
      showMonthlyReviewRecurrence: m && rev,
      showSeriesFrequency: s && st === 1 && !rev,
      showSeriesDates: s && st === 2 && !rev,
      showSeriesProrate: s && st === 3 && !rev,
      showSeriesReviewRecurrence: s && rev,
      showNotes: (q && quickMoreOpen) || rev,
      showReceiptAttachment: ((q && quickMoreOpen) || rev) && !values.isRecurring,
      showPaidNow: q || rev,
      showSubmitRow: q || rev,
      showWizardNav: isWizardFlow && !rev,
      showManualBanner: q || m || s,
      showSharedSplitCard:
        values.shared &&
        ((q && quickMoreOpen) || (m && (st === 4 || rev)) || (s && (st === 4 || rev))),
    };
  }, [
    createFlow,
    wizardStep,
    atWizardReview,
    quickMoreOpen,
    values.shared,
    values.isRecurring,
    isWizardFlow,
  ]);

  const quickFieldOrder = createFlow === "quick";

  /** Vista previa: cargo mensual fijo (ventana por defecto) vs serie con fin. */
  const recurrencePreview = useMemo(() => {
    if (!values.isRecurring) return null;

    if (values.recurrenceKind === "monthly_fixed") {
      const endsVirtual = endsOnForMonthlyFixedWindow(values.occurredOn);
      const dates = listOccurrenceDates(values.occurredOn, endsVirtual, 1);
      if (dates.length === 0) return { kind: "range_invalid" as const };
      const totalMinor = parseAmountToMinor(values.amountInput);
      if (totalMinor === null) return { kind: "need_amount" as const };
      return {
        kind: "ok_monthly_fixed" as const,
        count: dates.length,
        installmentMinor: totalMinor,
      };
    }

    if (values.recurrenceKind === "series") {
      const endsTrim = values.endsOn?.trim() ?? "";
      if (!values.prorated && !endsTrim) {
        const todayCap = addCalendarMonths(
          todayLocalIsoDate(),
          EXPENSE_SERIES_MATERIALIZE_MONTHS_INITIAL,
        );
        const step = monthsStepForFrequency(values.frequency);
        const dates = listOccurrenceDates(values.occurredOn, todayCap, step);
        if (dates.length === 0) return { kind: "range_invalid" as const };
        const totalMinor = parseAmountToMinor(values.amountInput);
        if (totalMinor === null) return { kind: "need_amount" as const };
        return {
          kind: "ok_series_open" as const,
          count: dates.length,
          installmentMinor: totalMinor,
        };
      }

      const stepOpen = monthsStepForFrequency(values.frequency);
      if (values.prorated && !endsTrim && stepOpen > 1) {
        const todayCap = addCalendarMonths(
          todayLocalIsoDate(),
          EXPENSE_SERIES_MATERIALIZE_MONTHS_INITIAL,
        );
        const dates = listOccurrenceDates(values.occurredOn, todayCap, 1);
        if (dates.length === 0) return { kind: "range_invalid" as const };
        const totalMinor = parseAmountToMinor(values.amountInput);
        if (totalMinor === null) return { kind: "need_amount" as const };
        const parts = splitTotalMinorAcrossOccurrences(totalMinor, stepOpen);
        return {
          kind: "ok_series_open_prorated" as const,
          count: dates.length,
          installmentMinor: parts[0] ?? 0,
          periodMonths: stepOpen,
        };
      }
    }

    const endsOn = values.endsOn?.trim() ?? "";
    if (!endsOn) return null;
    const { occurredOn, frequency, amountInput, prorated } = values;
    const step = monthsStepForFrequency(frequency);
    const dates = listOccurrenceDates(occurredOn, endsOn, step);
    const rangeInvalid = endsOn < occurredOn || dates.length === 0;
    if (rangeInvalid) return { kind: "range_invalid" as const };

    const totalMinor = parseAmountToMinor(amountInput);
    if (totalMinor === null) return { kind: "need_amount" as const };

    const n = dates.length;
    const monthSpan = inclusiveCalendarMonthSpan(occurredOn, endsOn);
    const monthlyAverageMinor = Math.round(totalMinor / monthSpan);

    if (prorated) {
      const parts = splitTotalMinorAcrossOccurrences(totalMinor, n);
      const installmentMinor = parts[0] ?? 0;
      return {
        kind: "ok_series" as const,
        count: n,
        installmentMinor,
        monthlyAverageMinor,
        prorated: true,
      };
    }
    return {
      kind: "ok_series" as const,
      count: n,
      installmentMinor: totalMinor,
      monthlyAverageMinor,
      prorated: false,
    };
  }, [
    values.isRecurring,
    values.recurrenceKind,
    values.endsOn,
    values.occurredOn,
    values.frequency,
    values.amountInput,
    values.prorated,
  ]);

  /** Importe base para reparto (un mes / una cuota), alineado con lo que se guardará. */
  const previewBaseMinor = useMemo(() => {
    if (!values.shared || members.length === 0) return null;
    const rp = recurrencePreview;
    if (
      rp?.kind === "ok_monthly_fixed" ||
      rp?.kind === "ok_series" ||
      rp?.kind === "ok_series_open" ||
      rp?.kind === "ok_series_open_prorated"
    ) {
      return rp.installmentMinor;
    }
    if (!values.isRecurring) {
      return parseAmountToMinor(values.amountInput);
    }
    return null;
  }, [values.shared, values.isRecurring, recurrencePreview, values.amountInput, members.length]);

  const sharedSplitRows = useMemo(() => {
    if (previewBaseMinor === null || previewBaseMinor < 0) return null;
    const splits = expenseSplitsFromMembers(members);
    return splitMinorByShareBps(previewBaseMinor, splits, (uid) => {
      const m = members.find((x) => x.userId === uid);
      return m?.user.name ?? uid;
    });
  }, [previewBaseMinor, members]);

  const submitMutation = useMutation({
    mutationFn: async (formValues: ExpenseNewFormValues) => {
      if (!houseId) throw new Error("no house");
      const catName =
        categories.find((c) => c.id === formValues.categoryId)?.name?.trim() ?? "";
      const titleResolved = formValues.title.trim() || catName;
      const amountMinor = parseAmountToMinor(formValues.amountInput);
      if (amountMinor === null) throw new Error("invalid_amount");
      const ccy = formValues.currency.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(ccy)) throw new Error("invalid_currency");
      const splits =
        formValues.shared && members.length > 0 ? expenseSplitsFromMembers(members) : undefined;
      const recurringSplitSource: "house" | "fixed" = formValues.shared ? "house" : "fixed";
      const contributionScope = formValues.shared ? "shared" : "personal";
      const pmOpt: ExpensePaymentMethod | undefined =
        formValues.paymentMethod === "" ? undefined : formValues.paymentMethod;

      const payer = paidByPayloadFromSelectValue(formValues.paidByUserId);

      if (formValues.isRecurring) {
        if (formValues.recurrenceKind === "monthly_fixed") {
          return createRecurringExpenses(locale, houseId, {
            title: titleResolved,
            totalAmountMinor: amountMinor,
            currency: ccy,
            occurredOn: formValues.occurredOn,
            endsOn: endsOnForMonthlyFixedWindow(formValues.occurredOn),
            frequency: "monthly",
            prorated: false,
            categoryId: formValues.categoryId || null,
            ...payer,
            notes: formValues.notes.trim() || null,
            paid: formValues.paidNow,
            splitSource: recurringSplitSource,
            contributionScope,
            ...(pmOpt ? { paymentMethod: pmOpt } : {}),
            splits,
          });
        }
        return createRecurringExpenses(locale, houseId, {
          title: titleResolved,
          totalAmountMinor: amountMinor,
          currency: ccy,
          occurredOn: formValues.occurredOn,
          endsOn: formValues.endsOn?.trim() || null,
          frequency: formValues.frequency,
          prorated: formValues.prorated,
          categoryId: formValues.categoryId || null,
          ...payer,
          notes: formValues.notes.trim() || null,
          paid: formValues.paidNow,
          splitSource: recurringSplitSource,
          contributionScope,
          ...(pmOpt ? { paymentMethod: pmOpt } : {}),
          splits,
        });
      }

      return createExpense(locale, houseId, {
        title: titleResolved,
        amountMinor,
        currency: ccy,
        occurredOn: formValues.occurredOn,
        categoryId: formValues.categoryId || null,
        ...payer,
        notes: formValues.notes.trim() || null,
        paid: formValues.paidNow,
        ...(pmOpt ? { paymentMethod: pmOpt } : {}),
        ...(receiptStorageKey ? { receiptStorageKey } : {}),
        contributionScope,
        splits,
      });
    },
    onSuccess: async (data) => {
      void trackDashboardEvent("expense_created", {
        platform: "web",
        flow: createFlow,
        recurring: values.isRecurring,
      });
      if (!houseId) return;
      const expenseId =
        "expenses" in data && data.expenses[0]?.id
          ? data.expenses[0].id
          : "id" in data && typeof data.id === "string"
            ? data.id
            : undefined;
      if (fromScan && expenseId) {
        try {
          await patchReceiptScan(locale, houseId, fromScan, { linkedExpenseId: expenseId });
          await queryClient.invalidateQueries({
            queryKey: homeExpenseQueryKeys.receiptScans(locale, houseId),
          });
        } catch {
          /* no bloquear el flujo principal */
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "expenses"] });
      if ("expenses" in data && data.expenses[0]) {
        void navigate(dashboardPaths.houseExpenseDetail(houseId, data.expenses[0].id));
        return;
      }
      if ("id" in data && typeof data.id === "string") {
        void navigate(dashboardPaths.houseExpenseDetail(houseId, data.id));
      }
    },
    onError: (err: unknown) => {
      void trackDashboardEvent("expense_create_failed", {
        platform: "web",
        flow: createFlow,
      });
      if (err instanceof ApiRequestError) {
        setError("root", { message: err.message });
        return;
      }
      if (err instanceof Error && err.message === "invalid_amount") {
        setError("amountInput", { message: t("dashboardUi.expenses.form.invalidAmount") });
        return;
      }
      setError("root", { message: t("dashboardUi.auth.errors.generic") });
    },
  });

  const onValid = (formValues: ExpenseNewFormValues): void => {
    clearErrors("root");
    void trackDashboardEvent("expense_create_started", {
      platform: "web",
      flow: createFlow,
      recurring: formValues.isRecurring,
    });
    submitMutation.mutate(formValues);
  };

  const loadingDeps = profileQuery.isLoading || categoriesQuery.isLoading || membersQuery.isLoading;
  const submitDisabled =
    submitMutation.isPending ||
    !resolvedTitle ||
    (isWizardFlow && wizardStep < EXPENSE_CREATE_WIZARD_REVIEW_STEP);

  const setFlowParam = (flow: ExpenseCreateFlow | null): void => {
    if (flow && flow !== "pick") {
      writeLastExpenseCreateFlow(flow);
      setSavedLastFlow(flow);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (flow && flow !== "pick") next.set("flow", flow);
        else next.delete("flow");
        return next;
      },
      { replace: true },
    );
  };

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  if (createFlow === "pick" && !fromScan) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseExpenses(houseId)}>
          {t("dashboardUi.expenses.backToJournal")}
        </DashboardBackLink>
        <h1 className="font-headline text-headline-sm font-bold text-primary">
          {t("dashboardUi.expenses.flows.launcherTitle")}
        </h1>
        <p className="mt-2 max-w-2xl text-body-md text-on-surface-variant">
          {t("dashboardUi.expenses.flows.launcherSubtitle")}
        </p>
        {loadingDeps ? (
          <div className="mt-8">
            <LoadingState />
          </div>
        ) : (
          <div className="mt-8 grid max-w-3xl gap-4">
            {savedLastFlow && savedLastFlow !== "pick" ? (
              <button
                type="button"
                className="group flex items-center gap-3 rounded-xl border border-primary/25 bg-primary-container/10 px-5 py-3 text-left text-body-md font-medium text-primary outline-none ring-primary transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-container/20 hover:shadow-sm focus-visible:ring-2"
                onClick={() => setFlowParam(savedLastFlow)}
              >
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon name={expenseFlowIcon(savedLastFlow)} className="size-5" aria-hidden />
                </span>
                {t("dashboardUi.expenses.flows.repeatLastFlowCta", {
                  flowLabel: t(expenseFlowTitleKey(savedLastFlow)),
                })}
              </button>
            ) : null}
            <button
              type="button"
              className="group rounded-2xl border border-outline-variant/20 bg-surface px-5 py-4 text-left outline-none ring-primary transition-all duration-200 hover:-translate-y-0.5 hover:bg-surface-container-high hover:shadow-md focus-visible:ring-2"
              onClick={() => setFlowParam("quick")}
            >
              <span className="flex items-start gap-3">
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform duration-200 group-hover:scale-105">
                  <Icon name="ReceiptText" className="size-5" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="block font-headline text-title-md font-semibold text-primary">
                    {t("dashboardUi.expenses.flows.intentQuickTitle")}
                  </span>
                  <span className="mt-1 block text-body-md text-on-surface-variant">
                    {t("dashboardUi.expenses.flows.intentQuickDescription")}
                  </span>
                </span>
              </span>
            </button>
            <button
              type="button"
              className="group rounded-2xl border border-outline-variant/20 bg-surface px-5 py-4 text-left outline-none ring-primary transition-all duration-200 hover:-translate-y-0.5 hover:bg-surface-container-high hover:shadow-md focus-visible:ring-2"
              onClick={() => setFlowParam("monthly")}
            >
              <span className="flex items-start gap-3">
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary/15 text-secondary transition-transform duration-200 group-hover:scale-105">
                  <Icon name="Repeat" className="size-5" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="block font-headline text-title-md font-semibold text-primary">
                    {t("dashboardUi.expenses.flows.intentRecurringTitle")}
                  </span>
                  <span className="mt-1 block text-body-md text-on-surface-variant">
                    {t("dashboardUi.expenses.flows.intentRecurringDescription")}
                  </span>
                </span>
              </span>
            </button>
            <button
              type="button"
              className="group rounded-2xl border border-outline-variant/20 bg-surface px-5 py-4 text-left outline-none ring-primary transition-all duration-200 hover:-translate-y-0.5 hover:bg-surface-container-high hover:shadow-md focus-visible:ring-2"
              onClick={() => setFlowParam("series")}
            >
              <span className="flex items-start gap-3">
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-tertiary/15 text-tertiary transition-transform duration-200 group-hover:scale-105">
                  <Icon name="CalendarRange" className="size-5" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="block font-headline text-title-md font-semibold text-primary">
                    {t("dashboardUi.expenses.flows.intentSeriesTitle")}
                  </span>
                  <span className="mt-1 block text-body-md text-on-surface-variant">
                    {t("dashboardUi.expenses.flows.intentSeriesDescription")}
                  </span>
                </span>
              </span>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.houseExpenses(houseId)}>
        {t("dashboardUi.expenses.backToJournal")}
      </DashboardBackLink>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-headline text-headline-sm font-bold text-primary">
          {createFlow === "quick"
            ? t("dashboardUi.expenses.flows.intentQuickTitle")
            : createFlow === "monthly"
              ? t("dashboardUi.expenses.flows.intentRecurringTitle")
              : t("dashboardUi.expenses.flows.intentSeriesTitle")}
        </h1>
        {!fromScan ? (
          <button
            type="button"
            className="text-left text-label-md font-medium text-primary hover:underline sm:text-right"
            onClick={() => {
              setWizardStep(0);
              setFlowParam(null);
            }}
          >
            {t("dashboardUi.expenses.flows.launcherTitle")}
          </button>
        ) : null}
      </div>
      {isWizardFlow ? (
        <p className="mt-1 font-mono text-label-md text-on-surface-variant">
          {wizardStep + 1}/{EXPENSE_CREATE_WIZARD_REVIEW_STEP + 1}
        </p>
      ) : null}

      {loadingDeps ? (
        <div className="mt-8">
          <LoadingState />
        </div>
      ) : (
        <form
          className="mt-8 flex flex-col gap-6 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15"
          onSubmit={handleSubmit(onValid)}
          noValidate
        >
          {fromScan ? (
            <p
              className="rounded-xl border border-secondary/25 bg-secondary-container/15 px-4 py-3 text-body-md text-on-surface"
              role="status"
            >
              {t("dashboardUi.scan.fromScanBanner")}
            </p>
          ) : null}
          {wizardBanner ? (
            <div className="rounded-xl border border-outline-variant/20 bg-surface-container px-4 py-3">
              <p className="font-headline text-title-sm font-semibold text-on-surface">{wizardBanner.title}</p>
              <p className="mt-1 text-body-md text-on-surface-variant">{wizardBanner.help}</p>
            </div>
          ) : null}
          {atWizardReview ? (
            <div className="rounded-xl border border-primary/20 bg-primary-container/10 px-4 py-3">
              <p className="font-headline text-title-sm font-semibold text-primary">
                {createFlow === "monthly"
                  ? t("dashboardUi.expenses.flows.monthlyWizard.reviewTitle")
                  : t("dashboardUi.expenses.flows.seriesWizard.reviewTitle")}
              </p>
              <p className="mt-1 text-body-md text-on-surface-variant">
                {createFlow === "monthly"
                  ? t("dashboardUi.expenses.flows.monthlyWizard.reviewHelp")
                  : t("dashboardUi.expenses.flows.seriesWizard.reviewHelp")}
              </p>
            </div>
          ) : null}
          <div className="flex flex-col gap-6">
          {stepUi.showAmount ? (
          <div className={quickFieldOrder ? "order-1" : ""}>
            <label htmlFor="exp-amount" className="text-label-md text-on-surface-variant">
              {values.isRecurring && values.recurrenceKind === "monthly_fixed"
                ? t("dashboardUi.expenses.form.amountLabelMonthly")
                : t("dashboardUi.expenses.form.amountLabel")}
            </label>
            <input
              id="exp-amount"
              {...register("amountInput")}
              inputMode="decimal"
              className="mt-2 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-4 font-headline text-3xl font-bold text-on-surface outline-none focus:border-primary"
              placeholder="0,00"
              aria-describedby="exp-amount-hint"
              aria-invalid={errors.amountInput ? true : undefined}
            />
            <p id="exp-amount-hint" className="mt-1 text-label-md text-on-surface-variant">
              {values.isRecurring && values.recurrenceKind === "monthly_fixed"
                ? t("dashboardUi.expenses.form.amountHintMonthlyFixed")
                : t("dashboardUi.expenses.form.amountHint")}
            </p>
            {errors.amountInput ? (
              <p className="mt-1 text-body-md text-error" role="alert">
                {errors.amountInput.message}
              </p>
            ) : null}
          </div>
          ) : null}

          {(stepUi.showCurrencyOnlyRow || stepUi.showDateOnlyRow || stepUi.showCurrencyDateGrid) ? (
          <div
            className={`${quickFieldOrder ? "order-4" : ""} grid gap-4 ${stepUi.showCurrencyOnlyRow || stepUi.showDateOnlyRow ? "sm:grid-cols-1" : "sm:grid-cols-2"}`}
          >
            {(stepUi.showCurrencyOnlyRow || stepUi.showCurrencyDateGrid) ? (
            <div>
              <label htmlFor="exp-currency" className="text-label-md text-on-surface-variant">
                {t("dashboardUi.expenses.form.currencyLabel")}
              </label>
              <input
                id="exp-currency"
                {...register("currency")}
                maxLength={3}
                className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-mono text-body-md uppercase text-on-surface outline-none focus:border-primary"
                aria-invalid={errors.currency ? true : undefined}
              />
              {errors.currency ? (
                <p className="mt-1 text-body-md text-error" role="alert">
                  {errors.currency.message}
                </p>
              ) : null}
            </div>
            ) : null}
            {(stepUi.showDateOnlyRow || stepUi.showCurrencyDateGrid) ? (
            <div>
              <label htmlFor="exp-date" className="text-label-md text-on-surface-variant">
                {t("dashboardUi.expenses.form.dateLabel")}
              </label>
              <input
                id="exp-date"
                type="date"
                {...register("occurredOn")}
                required
                className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
              />
            </div>
            ) : null}
          </div>
          ) : null}

          {stepUi.showCategory ? (
          <div className={quickFieldOrder ? "order-2" : ""}>
            <p className="text-label-md text-on-surface-variant">{t("dashboardUi.expenses.form.categoryLabel")}</p>
            <div
              className="mt-2 flex flex-wrap gap-2"
              role="radiogroup"
              aria-label={t("dashboardUi.expenses.form.categoryLabel")}
            >
              <button
                type="button"
                role="radio"
                aria-checked={values.categoryId === ""}
                onClick={() =>
                  setValue("categoryId", "", { shouldValidate: true, shouldDirty: true })
                }
                className={`rounded-full px-4 py-2 text-body-md outline outline-1 transition-colors ${
                  values.categoryId === ""
                    ? "bg-primary text-on-primary outline-primary"
                    : "bg-surface text-on-surface outline-outline-variant/15 hover:bg-surface-container-high"
                }`}
              >
                {t("dashboardUi.expenses.form.categoryNone")}
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={values.categoryId === c.id}
                  onClick={() =>
                    setValue("categoryId", c.id, { shouldValidate: true, shouldDirty: true })
                  }
                  className={`rounded-full px-4 py-2 text-body-md outline outline-1 transition-colors ${
                    values.categoryId === c.id
                      ? "bg-primary text-on-primary outline-primary"
                      : "bg-surface text-on-surface outline-outline-variant/15 hover:bg-surface-container-high"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          ) : null}

          {stepUi.showTitle ? (
          <div className={quickFieldOrder ? "order-6" : ""}>
            <label htmlFor="exp-title" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.titleLabel")}
            </label>
            <input
              id="exp-title"
              {...register("title")}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
              placeholder={t("dashboardUi.expenses.form.titlePlaceholder")}
              aria-invalid={errors.title ? true : undefined}
            />
            <p className="mt-1 text-label-md text-on-surface-variant">{t("dashboardUi.expenses.form.titleOptionalHint")}</p>
            {errors.title ? (
              <p className="mt-1 text-body-md text-error" role="alert">
                {errors.title.message}
              </p>
            ) : null}
          </div>
          ) : null}

          {stepUi.showQuickMoreToggle ? (
            <button
              type="button"
              className={`text-left text-label-md font-medium text-primary hover:underline ${quickFieldOrder ? "order-5" : ""}`}
              onClick={() => setQuickMoreOpen((v) => !v)}
            >
              {t("dashboardUi.expenses.flows.quickExpandMore")}
            </button>
          ) : null}

          {stepUi.showPayment ? (
          <div className={quickFieldOrder ? "order-3" : ""}>
            <label htmlFor="exp-pay-method" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.paymentMethodLabel")}
            </label>
            <select
              id="exp-pay-method"
              {...register("paymentMethod")}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
            >
              <option value="">{t("dashboardUi.expenses.form.paymentMethodNone")}</option>
              <option value="card">{t("dashboardUi.expenses.form.paymentMethodCard")}</option>
              <option value="cash">{t("dashboardUi.expenses.form.paymentMethodCash")}</option>
              <option value="account">{t("dashboardUi.expenses.form.paymentMethodAccount")}</option>
            </select>
          </div>
          ) : null}

          {stepUi.showPayerShared ? (
            <div className={`flex flex-col gap-6 ${quickFieldOrder ? "order-7" : ""}`}>
              <label className="flex cursor-pointer items-start gap-2 text-body-md text-on-surface">
                <input
                  type="checkbox"
                  {...register("shared")}
                  className="mt-1 size-4 shrink-0 rounded border-outline-variant text-primary"
                />
                <span>
                  <span className="block">{t("dashboardUi.expenses.form.sharedLabel")}</span>
                  <span className="mt-0.5 block text-label-md text-on-surface-variant">
                    {t("dashboardUi.expenses.form.sharedHint")}
                  </span>
                </span>
              </label>

              <div>
                <label htmlFor="exp-payer" className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.expenses.form.paidByLabel")}
                </label>
                <select
                  id="exp-payer"
                  {...register("paidByUserId")}
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
                >
                  <option value="">{t("dashboardUi.expenses.form.paidByDefault")}</option>
                  <option value={PAID_BY_JOINT_SELECT_VALUE}>
                    {t("dashboardUi.expenses.form.paidByJointOption")}
                  </option>
                  {members.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.user.name} ({m.user.email})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-label-md text-on-surface-variant">
                  {t("dashboardUi.expenses.form.paidByHint")}
                </p>
              </div>
            </div>
          ) : null}

          {stepUi.showSharedSplitCard && sharedSplitRows && sharedSplitRows.length > 0 ? (
            <div className={quickFieldOrder ? "order-8" : ""}>
            <ExpenseSplitBreakdownCard
              title={t("dashboardUi.expenses.form.sharedSplitPreviewTitle")}
              subtitle={t("dashboardUi.expenses.form.sharedSplitPreviewSubtitle")}
              rows={sharedSplitRows}
              currency={values.currency}
              locale={locale}
              modeLabel={
                membersUseConfiguredSplitBps(members)
                  ? t("dashboardUi.expenses.form.sharedSplitUsesHousePercent")
                  : t("dashboardUi.expenses.form.sharedSplitEqualParts")
              }
              modeLabelTone={membersUseConfiguredSplitBps(members) ? "accent" : "muted"}
            />
            </div>
          ) : null}
          </div>

          {stepUi.showSeriesFrequency ? (
            <div className="flex flex-col gap-4 border-t border-outline-variant/15 pt-4">
              <div className="rounded-xl border border-outline-variant/20 bg-surface-container px-4 py-3 text-body-md text-on-surface-variant">
                <p>{t("dashboardUi.expenses.form.seriesSingleChargeHint")}</p>
                <button
                  type="button"
                  className="mt-2 text-label-md font-medium text-primary hover:underline"
                  onClick={() => {
                    setValue("frequency", "annual", { shouldValidate: true });
                    setValue("prorated", false, { shouldValidate: true });
                    setValue("endsOn", "", { shouldValidate: true });
                  }}
                >
                  {t("dashboardUi.expenses.form.seriesAnnualPresetAction")}
                </button>
              </div>
              <div>
                <label htmlFor="exp-freq-wizard" className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.expenses.form.recurringFrequencyLabel")}
                </label>
                <select
                  id="exp-freq-wizard"
                  {...register("frequency")}
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
                >
                  <option value="monthly">{t("dashboardUi.expenses.form.frequencyMonthly")}</option>
                  <option value="bimonthly">{t("dashboardUi.expenses.form.frequencyBimonthly")}</option>
                  <option value="quarterly">{t("dashboardUi.expenses.form.frequencyQuarterly")}</option>
                  <option value="semiannual">{t("dashboardUi.expenses.form.frequencySemiannual")}</option>
                  <option value="annual">{t("dashboardUi.expenses.form.frequencyAnnual")}</option>
                </select>
              </div>
            </div>
          ) : null}

          {stepUi.showSeriesDates ? (
            <div className="grid gap-4 border-t border-outline-variant/15 pt-4 sm:grid-cols-2">
              <div>
                <label htmlFor="exp-date-wizard" className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.expenses.form.dateLabel")}
                </label>
                <input
                  id="exp-date-wizard"
                  type="date"
                  {...register("occurredOn")}
                  required
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
                />
              </div>
              <div>
                <label htmlFor="exp-ends-wizard" className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.expenses.form.recurringEndsLabel")}
                </label>
                <input
                  id="exp-ends-wizard"
                  type="date"
                  {...register("endsOn")}
                  min={values.occurredOn}
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
                  aria-invalid={errors.endsOn ? true : undefined}
                />
                {errors.endsOn ? (
                  <p className="mt-1 text-body-md text-error" role="alert">
                    {errors.endsOn.message}
                  </p>
                ) : null}
                <p className="mt-1 text-label-md text-on-surface-variant">{seriesEndsFieldHelp}</p>
              </div>
            </div>
          ) : null}

          {stepUi.showSeriesProrate ? (
            <div className="flex flex-col gap-3 border-t border-outline-variant/15 pt-4">
              <label className="flex cursor-pointer items-start gap-2 text-body-md text-on-surface">
                <input
                  type="checkbox"
                  {...register("prorated")}
                  className="mt-1 size-4 shrink-0 rounded border-outline-variant text-primary"
                />
                <span>
                  <span className="block">{t("dashboardUi.expenses.form.proratedLabel")}</span>
                  <span className="mt-0.5 block text-label-md text-on-surface-variant">
                    {t("dashboardUi.expenses.form.proratedHint")}
                  </span>
                </span>
              </label>
            </div>
          ) : null}

          {stepUi.showMonthlyReviewRecurrence ? (
            <div className="flex flex-col gap-4 border-t border-outline-variant/15 pt-4">
              <div className="flex flex-col gap-2 text-body-md text-on-surface-variant">
                <p>
                  {t("dashboardUi.expenses.form.monthlyFixedPatternExplain", {
                    count: MONTHLY_FIXED_MATERIALIZED_COUNT,
                  })}
                </p>
                <p>{t("dashboardUi.expenses.form.smoothBimonthlyBillHint")}</p>
              </div>
              {recurrencePreview?.kind === "need_amount" ? (
                <p className="text-body-md text-on-surface-variant">
                  {t("dashboardUi.expenses.form.recurringPreviewNeedAmountMonthlyFixed")}
                </p>
              ) : null}
              {recurrencePreview?.kind === "ok_monthly_fixed" ? (
                <p className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.expenses.form.monthlyFixedHorizonNote", {
                    count: recurrencePreview.count,
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          {stepUi.showSeriesReviewRecurrence ? (
            <div className="flex flex-col gap-4 border-t border-outline-variant/15 pt-4">
              {recurrencePreview?.kind === "need_amount" ? (
                <p className="text-body-md text-on-surface-variant">
                  {t("dashboardUi.expenses.form.recurringPreviewNeedAmount")}
                </p>
              ) : null}
              {recurrencePreview?.kind === "ok_series" ? (
                <div className="flex flex-col gap-1 text-body-md text-on-surface-variant">
                  <p>
                    {t("dashboardUi.expenses.form.recurringSummary", {
                      count: recurrencePreview.count,
                      installment: formatMinorAsCurrency(
                        recurrencePreview.installmentMinor,
                        values.currency,
                        locale,
                      ),
                    })}
                  </p>
                  {recurrencePreview.prorated ? (
                    <p>
                      {t("dashboardUi.expenses.form.proratedMonthlyAverageHint", {
                        amount: formatMinorAsCurrency(
                          recurrencePreview.monthlyAverageMinor,
                          values.currency,
                          locale,
                        ),
                      })}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {recurrencePreview?.kind === "ok_series_open" ? (
                <div className="flex flex-col gap-1 text-body-md text-on-surface-variant">
                  <p>
                    {t("dashboardUi.expenses.form.recurringSummary", {
                      count: recurrencePreview.count,
                      installment: formatMinorAsCurrency(
                        recurrencePreview.installmentMinor,
                        values.currency,
                        locale,
                      ),
                    })}
                  </p>
                  <p>
                    {t("dashboardUi.expenses.form.recurringOpenEndedSummary", {
                      months: EXPENSE_SERIES_MATERIALIZE_MONTHS_INITIAL,
                    })}
                  </p>
                </div>
              ) : null}
              {recurrencePreview?.kind === "ok_series_open_prorated" ? (
                <div className="flex flex-col gap-1 text-body-md text-on-surface-variant">
                  <p>
                    {t("dashboardUi.expenses.form.recurringSummary", {
                      count: recurrencePreview.count,
                      installment: formatMinorAsCurrency(
                        recurrencePreview.installmentMinor,
                        values.currency,
                        locale,
                      ),
                    })}
                  </p>
                  <p>
                    {t("dashboardUi.expenses.form.recurringProratedOpenPeriodPreviewNote", {
                      periodMonths: recurrencePreview.periodMonths,
                    })}
                  </p>
                  <p>
                    {t("dashboardUi.expenses.form.recurringOpenEndedSummary", {
                      months: EXPENSE_SERIES_MATERIALIZE_MONTHS_INITIAL,
                    })}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {stepUi.showNotes ? (
          <div>
            <label htmlFor="exp-notes" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.notesLabel")}
            </label>
            <textarea
              id="exp-notes"
              {...register("notes")}
              rows={3}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
              placeholder={t("dashboardUi.expenses.form.notesPlaceholder")}
            />
          </div>
          ) : null}

          {stepUi.showReceiptAttachment && houseId ? (
            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 p-4">
              <p className="text-label-md text-on-surface">{t("dashboardUi.expenses.form.receiptLabel")}</p>
              <p className="mt-1 text-body-sm text-on-surface-variant">
                {t("dashboardUi.expenses.form.receiptHelp")}
              </p>
              <div className="mt-3" key={receiptPondKey}>
                <FilePondStorageUpload
                  locale={locale}
                  mode="vault"
                  maxFiles={1}
                  inputId={`expense-receipt-upload-${receiptPondKey}`}
                  onStoredKeys={(keys) => {
                    setReceiptStorageKey(keys[0] ?? null);
                  }}
                  disabled={submitMutation.isPending}
                />
              </div>
              {receiptStorageKey ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setReceiptStorageKey(null);
                    setReceiptPondKey((k) => k + 1);
                  }}
                >
                  {t("dashboardUi.expenses.form.receiptRemove")}
                </Button>
              ) : null}
            </div>
          ) : null}

          {stepUi.showPaidNow ? (
          <label className="flex cursor-pointer items-center gap-2 text-body-md text-on-surface">
            <input type="checkbox" {...register("paidNow")} className="size-4 rounded border-outline-variant text-primary" />
            {t("dashboardUi.expenses.form.paidNowLabel")}
          </label>
          ) : null}

          {errors.root ? (
            <p className="text-body-md text-error" role="alert">
              {errors.root.message}
            </p>
          ) : null}

          {stepUi.showWizardNav ? (
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={wizardStep <= 0}
                onClick={() => setWizardStep((s) => Math.max(0, s - 1))}
              >
                {t("dashboardUi.expenses.flows.navBack")}
              </Button>
              <Button
                type="button"
                disabled={wizardStep >= EXPENSE_CREATE_WIZARD_REVIEW_STEP}
                onClick={() =>
                  setWizardStep((s) => Math.min(EXPENSE_CREATE_WIZARD_REVIEW_STEP, s + 1))
                }
              >
                {t("dashboardUi.expenses.flows.navNext")}
              </Button>
            </div>
          ) : null}

          {stepUi.showSubmitRow ? (
          <Button type="submit" disabled={submitDisabled}>
            {submitMutation.isPending
              ? t("dashboardUi.expenses.form.saving")
              : values.isRecurring
                ? t("dashboardUi.expenses.form.submitRecurring")
                : t("dashboardUi.expenses.form.submitCreate")}
          </Button>
          ) : null}
        </form>
      )}
    </div>
  );
}
